import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert, 
  AlertTitle, 
  Stack,
  Link
} from '@mui/material';
import { 
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon 
} from '@mui/icons-material';
import { importExcelToFirestore } from '../services/excelImport';

interface PatientExcelUploadProps {
  onUploadSuccess?: (count: number) => void;
  onUploadError?: (message: string) => void;
}

interface UploadStatus {
  success: boolean;
  message: string;
  count?: number;
  updatedCount?: number;
  addedCount?: number;
}

const PatientExcelUpload: React.FC<PatientExcelUploadProps> = ({ 
  onUploadSuccess, 
  onUploadError 
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus | null>(null);

  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    
    // Clear previous status
    setUploadStatus(null);
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!file) {
      setUploadStatus({
        success: false,
        message: 'Please select an Excel file first.'
      });
      return;
    }

    // Validate file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
      setUploadStatus({
        success: false,
        message: 'Please select a valid Excel file (.xlsx, .xls) or CSV file (.csv).'
      });
      return;
    }

    setIsUploading(true);
    setUploadStatus(null);

    try {
      const result = await importExcelToFirestore(file);
      setUploadStatus({
        success: result.success,
        message: result.message,
        count: result.count,
        updatedCount: result.updatedCount,
        addedCount: result.addedCount
      });

      if (result.success && result.count && onUploadSuccess) {
        onUploadSuccess(result.count);
      } else if (!result.success && onUploadError) {
        onUploadError(result.message);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setUploadStatus({
        success: false,
        message: `Upload failed: ${errorMessage}`
      });

      if (onUploadError) {
        onUploadError(errorMessage);
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Stack spacing={3}>
        <Typography variant="h6" gutterBottom>
          Import Patient Data from Excel or CSV
        </Typography>
        
        <Typography variant="body2" color="text.secondary">
          Upload an Excel file (.xlsx, .xls) or CSV file (.csv) containing patient data.
          <Box mt={1}>
            <Link 
              href="/patient_data_template.csv" 
              download="patient_data_template.csv"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <DownloadIcon fontSize="small" />
              Download CSV Template
            </Link>
          </Box>
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Select File
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              hidden
              onChange={handleFileChange}
              disabled={isUploading}
            />
          </Button>
          
          {file && (
            <Typography variant="body2">
              Selected file: {file.name}
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={!file || isUploading}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {isUploading ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Uploading...
              </>
            ) : (
              'Upload & Import'
            )}
          </Button>
        </Box>

        {uploadStatus && (
          <Alert severity={uploadStatus.success ? 'success' : 'error'}>
            <AlertTitle>{uploadStatus.success ? 'Success' : 'Error'}</AlertTitle>
            <Typography variant="body2" gutterBottom>{uploadStatus.message}</Typography>
            
            {uploadStatus.success && uploadStatus.count && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Total:</strong> {uploadStatus.count} patients
                </Typography>
                {uploadStatus.updatedCount !== undefined && (
                  <Typography variant="body2">
                    <strong>Updated:</strong> {uploadStatus.updatedCount} existing patients
                  </Typography>
                )}
                {uploadStatus.addedCount !== undefined && (
                  <Typography variant="body2">
                    <strong>Added:</strong> {uploadStatus.addedCount} new patients
                  </Typography>
                )}
              </Box>
            )}
          </Alert>
        )}
      </Stack>
    </Paper>
  );
};

export default PatientExcelUpload; 